import { AxisBottom, AxisLeft, AxisRight } from '@visx/axis';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear } from '@visx/scale';
import { BarRounded, LinePath } from '@visx/shape';
import React from 'react';
import { integerCountAxis } from './contentHealth.utils';

/** One period of the chart: how many pages it counted and their average health (0–100). */
export type ContentHealthTrendPeriod = {
  id: string;
  label: string;
  count: number;
  /** Absent when the period counted no page, so the line breaks there instead of dropping to 0 %. */
  health?: number;
};

/** One tag's line, with a value per period in the order of the periods. */
export type ContentHealthTagSeries = {
  key: string;
  label: string;
  color: string;
  values: { count: number; health?: number }[];
};

/** The colour of the health line in the combined view: the product's primary blue. */
const HEALTH_LINE_COLOR = '#009EE3';

/** slate-500: the axis labels, readable on white. */
const AXIS_LABEL_COLOR = '#64748B';

/** slate-300: the axis lines and tick marks, quieter than their labels. */
const AXIS_LINE_COLOR = '#CBD5E1';

/** The axis labels' size in pixels. */
const AXIS_LABEL_SIZE = 12;

const HEALTH_TICKS = [0, 25, 50, 75, 100];

/** The fewest periods with pages a trend needs; one point is a figure, not a trend. */
export const MIN_TREND_PERIODS = 2;

function tagValueText(value: { count: number; health?: number }, countNoun: string): string {
  if (!value.count || value.health === undefined) {
    return `No ${countNoun}`;
  }

  return `${value.health}% · ${value.count} ${countNoun}`;
}

function periodDescription({
  period,
  index,
  tagSeries,
  countNoun,
}: {
  period: ContentHealthTrendPeriod;
  index: number;
  tagSeries?: ContentHealthTagSeries[];
  countNoun: string;
}): string {
  if (tagSeries) {
    return [period.label, ...tagSeries.map((series) => `${series.label}: ${tagValueText(series.values[index], countNoun)}`)].join(', ');
  }

  if (!period.count || period.health === undefined) {
    return `${period.label}: no ${countNoun}`;
  }

  return `${period.label}: ${period.count} ${countNoun}, average health ${period.health}%`;
}

/**
 * The Content Health trend: in the combined view the pages per period as bars and their average
 * health as a line; per tag, one health line per tag. Hovering or focusing a period shows its exact
 * figures.
 */
export function ContentHealthTrendsChart({
  periods,
  tagSeries,
  countNoun,
  periodNoun,
}: {
  periods: ContentHealthTrendPeriod[];
  /** Set in the per-tag view: lines only, one per tag. */
  tagSeries?: ContentHealthTagSeries[];
  /** What the bars count, in the plural: "pages". */
  countNoun: string;
  /** What one bar stands for, in the plural: "months" or "weeks". */
  periodNoun: string;
}) {
  const [activeIndex, setActiveIndex] = React.useState<number | undefined>(undefined);

  const periodsWithData = tagSeries
    ? periods.filter((_period, index) => tagSeries.some((series) => series.values[index]?.count > 0)).length
    : periods.filter((period) => period.count > 0 && period.health !== undefined).length;

  if (periodsWithData < MIN_TREND_PERIODS) {
    return (
      <div className="flex h-[320px] flex-col items-center justify-center gap-1 rounded-xl bg-slate-50 px-6 text-center">
        <p className="m-0 text-sm font-semibold text-slate-700">Not enough history yet</p>
        <p className="m-0 max-w-md text-center text-sm text-slate-500">
          {`The trend appears once ${countNoun} have been checked in at least ${MIN_TREND_PERIODS} ${periodNoun}.`}
        </p>
      </div>
    );
  }

  const labels = periods.map((period) => period.label);
  const activePeriod = activeIndex === undefined ? undefined : periods[activeIndex];

  return (
    <div className="flex flex-col">
      <ul className="m-0 mb-3 flex list-none flex-wrap items-center gap-x-5 gap-y-2 p-0 text-xs text-slate-600">
        {tagSeries ? (
          <>
            <li className="text-slate-500">Lines: average health of each tag</li>
            {tagSeries.map((series) => (
              <li key={`legend:${series.key}`} className="flex items-center gap-2">
                {/* The swatch is the series' own colour, known only at runtime. */}
                <span aria-hidden="true" className="inline-block h-0.5 w-4 rounded-full" style={{ backgroundColor: series.color }} />
                {series.label}
              </li>
            ))}
          </>
        ) : (
          <>
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-3 w-3 rounded-t-[3px] bg-sky-200" />
              {`Bars: ${countNoun}`}
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-0.5 w-4 rounded-full bg-primary" />
              Line: average health
            </li>
          </>
        )}
      </ul>

      {/* A definite height: `ParentSize` measures against it. */}
      <div className="relative h-[320px]" onMouseLeave={() => setActiveIndex(undefined)}>
        <ParentSize>
          {({ width, height }) => {
            const margin = { top: 24, right: tagSeries ? 24 : 56, bottom: 48, left: 56 };
            const innerWidth = Math.max(120, width - margin.left - margin.right);
            const innerHeight = Math.max(120, height - margin.top - margin.bottom);

            const xScale = scaleBand<string>({ domain: labels, range: [0, innerWidth], padding: 0.3 });
            const healthScale = scaleLinear<number>({ domain: [0, 100], range: [innerHeight, 0] });
            const countAxis = integerCountAxis(Math.max(0, ...periods.map((period) => period.count)));
            const countScale = scaleLinear<number>({ domain: [0, countAxis.max], range: [innerHeight, 0] });
            const centerOf = (label: string) => (xScale(label) ?? 0) + xScale.bandwidth() / 2;
            const step = xScale.step();

            const lines = tagSeries
              ? tagSeries.map((series) => ({
                  key: series.key,
                  label: series.label,
                  color: series.color,
                  points: periods.map((period, index) => ({
                    x: period.label,
                    health: series.values[index]?.count ? series.values[index]?.health : undefined,
                  })),
                }))
              : [
                  {
                    key: 'health',
                    label: 'Average health',
                    color: HEALTH_LINE_COLOR,
                    points: periods.map((period) => ({ x: period.label, health: period.count ? period.health : undefined })),
                  },
                ];

            return (
              <>
                <svg width={width} height={height} role="group" aria-label={`Trend over the last ${periods.length} ${periodNoun}`}>
                  <Group top={margin.top} left={margin.left}>
                    {HEALTH_TICKS.map((tick) => (
                      <line
                        key={`grid:${tick}`}
                        x1={0}
                        x2={innerWidth}
                        y1={healthScale(tick)}
                        y2={healthScale(tick)}
                        className="stroke-slate-100"
                        strokeWidth={1}
                      />
                    ))}

                    {activeIndex !== undefined ? (
                      <rect
                        x={(xScale(labels[activeIndex]) ?? 0) - (step - xScale.bandwidth()) / 2}
                        y={0}
                        width={step}
                        height={innerHeight}
                        rx={6}
                        className="fill-slate-100/80"
                      />
                    ) : null}

                    {tagSeries
                      ? null
                      : periods.map((period, index) => {
                          if (period.count <= 0) {
                            return null;
                          }
                          const y = countScale(period.count);
                          return (
                            <BarRounded
                              key={`bar:${period.id}`}
                              x={xScale(period.label) ?? 0}
                              y={y}
                              width={xScale.bandwidth()}
                              height={Math.max(0, innerHeight - y)}
                              radius={5}
                              top
                              className={index === activeIndex ? 'fill-sky-300' : 'fill-sky-200'}
                            />
                          );
                        })}

                    {lines.map((line) => (
                      <React.Fragment key={`line:${line.key}`}>
                        {/* `defined` breaks the line across a period without pages instead of drawing
                            it down to 0 %: an empty period has no health to plot. */}
                        <LinePath
                          data={line.points}
                          defined={(point) => point.health !== undefined}
                          x={(point) => centerOf(point.x)}
                          y={(point) => healthScale(point.health ?? 0)}
                          stroke={line.color}
                          strokeWidth={2.5}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                        {line.points.map((point, index) =>
                          point.health === undefined ? null : (
                            <circle
                              key={`point:${line.key}:${point.x}`}
                              cx={centerOf(point.x)}
                              cy={healthScale(point.health)}
                              r={index === activeIndex ? 5 : 4}
                              fill={line.color}
                              stroke="#FFFFFF"
                              strokeWidth={2}
                            />
                          )
                        )}
                      </React.Fragment>
                    ))}

                    <AxisLeft
                      scale={healthScale}
                      tickValues={HEALTH_TICKS}
                      tickFormat={(value) => `${value}%`}
                      stroke={AXIS_LINE_COLOR}
                      tickStroke={AXIS_LINE_COLOR}
                      tickLabelProps={() => ({ fill: AXIS_LABEL_COLOR, fontSize: AXIS_LABEL_SIZE, textAnchor: 'end', dy: '0.32em' })}
                    />
                    <text x={-12} y={-12} textAnchor="end" fill={AXIS_LABEL_COLOR} fontSize={AXIS_LABEL_SIZE} fontWeight={600}>
                      Health
                    </text>
                    {tagSeries ? null : (
                      <>
                        <AxisRight
                          scale={countScale}
                          left={innerWidth}
                          tickValues={countAxis.ticks}
                          tickFormat={(value) => String(value)}
                          stroke={AXIS_LINE_COLOR}
                          tickStroke={AXIS_LINE_COLOR}
                          tickLabelProps={() => ({
                            fill: AXIS_LABEL_COLOR,
                            fontSize: AXIS_LABEL_SIZE,
                            textAnchor: 'start',
                            dx: '0.35em',
                            dy: '0.32em',
                          })}
                        />
                        <text
                          x={innerWidth + 12}
                          y={-12}
                          textAnchor="start"
                          fill={AXIS_LABEL_COLOR}
                          fontSize={AXIS_LABEL_SIZE}
                          fontWeight={600}
                          className="capitalize"
                        >
                          {countNoun}
                        </text>
                      </>
                    )}
                    <AxisBottom
                      top={innerHeight}
                      scale={xScale}
                      stroke={AXIS_LINE_COLOR}
                      tickStroke={AXIS_LINE_COLOR}
                      tickLabelProps={() => ({ fill: AXIS_LABEL_COLOR, fontSize: AXIS_LABEL_SIZE, textAnchor: 'middle', dy: '0.6em' })}
                    />

                    {/* One target per period, on top of everything: the pointer or the keyboard picks a
                        period, and its figures appear in the tooltip and in the accessible name. */}
                    {periods.map((period, index) => (
                      <rect
                        key={`target:${period.id}`}
                        x={(xScale(period.label) ?? 0) - (step - xScale.bandwidth()) / 2}
                        y={0}
                        width={step}
                        height={innerHeight}
                        fill="transparent"
                        tabIndex={0}
                        role="img"
                        aria-label={periodDescription({ period, index, tagSeries, countNoun })}
                        className="cursor-default focus:outline-none"
                        onMouseEnter={() => setActiveIndex(index)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(undefined)}
                      />
                    ))}
                  </Group>
                </svg>

                {activePeriod && activeIndex !== undefined ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-2 z-10 min-w-[10rem] -translate-x-1/2 rounded-lg bg-white px-3 py-2 text-xs shadow-card-hover ring-1 ring-slate-200/80"
                    // The period's centre on the chart, known only once the chart is measured; kept
                    // inside the chart so the box never spills over the card's edge.
                    style={{
                      left: Math.min(Math.max(margin.left + centerOf(activePeriod.label), 90), Math.max(90, width - 90)),
                    }}
                  >
                    <p className="m-0 mb-1 font-semibold text-slate-900">{activePeriod.label}</p>
                    {tagSeries ? (
                      <ul className="m-0 list-none space-y-0.5 p-0">
                        {tagSeries.map((series) => (
                          <li key={`tooltip:${series.key}`} className="flex items-center justify-between gap-3 text-slate-600">
                            <span className="flex items-center gap-1.5">
                              {/* The series' own colour, known only at runtime. */}
                              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: series.color }} />
                              {series.label}
                            </span>
                            <span className="tabular-nums text-slate-900">{tagValueText(series.values[activeIndex], countNoun)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : activePeriod.count && activePeriod.health !== undefined ? (
                      <dl className="m-0 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-slate-600">
                        <dt className="font-normal capitalize">{countNoun}</dt>
                        <dd className="m-0 text-right tabular-nums text-slate-900">{activePeriod.count}</dd>
                        <dt className="font-normal">Average health</dt>
                        <dd className="m-0 text-right tabular-nums text-slate-900">{`${activePeriod.health}%`}</dd>
                      </dl>
                    ) : (
                      <p className="m-0 text-slate-500">{`No ${countNoun}`}</p>
                    )}
                  </div>
                ) : null}
              </>
            );
          }}
        </ParentSize>
      </div>
    </div>
  );
}
